<script setup lang="ts">
import { joinURL } from 'ufo';

// PROPS
const { salesContact, variant } = defineProps({
    salesContact: { type: Object as PropType<SalesContact>, required: true },
    variant: { type: String as PropType<'whatsappOnly' | 'whatsappEmail'>, default: 'whatsappEmail' },
});

// EMITS
defineEmits<{
    openContactForm: [];
}>();

// COMPOSABLES
const { tt } = useTypedI18n();
const { trackSalesContactPhoneLink } = useTracking();
const config = useRuntimeConfig();

// DATA
const profileImageUrl = computed(() =>
    salesContact.profileImage ? joinURL(config.public.cdnMediaBaseUrl, salesContact.profileImage) : '/fleequid-expert.webp'
);

// METHODS
const onPhoneClick = (): void => {
    trackSalesContactPhoneLink(salesContact.phone);
};
</script>

<template>
    <div class="space-y-4 rounded-xl bg-neutral-100 p-6">
        <div class="text-sm font-extrabold uppercase">{{ tt('salesContact.card.title') }}:</div>
        <div>
            <div class="flex gap-4">
                <UAvatar :src="profileImageUrl" :as="{ img: 'img' }" class="size-18 shrink-0" :ui="{ root: 'bg-white border border-neutral-300' }" />
                <div class="flex flex-col justify-center gap-1">
                    <p class="font-semibold">{{ salesContact.fullName }}</p>
                    <div v-if="salesContact.supportedLanguages?.length" class="flex gap-2">
                        <UIcon
                            v-for="language in salesContact.supportedLanguages"
                            :key="language"
                            class="text-lg"
                            :name="`i-circle-flags-${language}`"
                        />
                    </div>
                </div>
            </div>
            <div class="flex items-end justify-between gap-3">
                <div class="flex items-center gap-3">
                    <UIcon name="lucide:phone" class="text-primary size-5" />
                    <BasePhoneLink class="text-primary" :phone="salesContact.phone" no-tracking @click="onPhoneClick" />
                </div>
                <div class="flex gap-3">
                    <SdpSalesContactWhatsappButton :sales-contact="salesContact" />
                    <SdpSalesContactContactFormButton
                        v-if="variant === 'whatsappEmail'"
                        :sales-contact="salesContact"
                        @click="$emit('openContactForm')"
                    />
                </div>
            </div>
        </div>
    </div>
</template>
